/**
 * Service zum Abrufen von Wetterdaten von Visual Crossing
 */

/**
 * Ruft aktuelle Wetterdaten und Prognose ab
 * @returns {Promise<{current: Object, forecast: Array, weekend: Array}>} Wetterdaten
 */
export const fetchWeatherData = async () => {
    // API-Konfiguration aus Umgebungsvariablen
    const API_KEY = process.env.REACT_APP_VISUAL_CROSSING_API_KEY;
    const LOCATION = process.env.REACT_APP_WEATHER_LOCATION || 'braunschweig';
    
    if (!API_KEY) {
      console.error('API-Schlüssel nicht gefunden! Bitte REACT_APP_VISUAL_CROSSING_API_KEY in .env konfigurieren.');
      const fallbackKey = 'KC2SB2NEK64XXVU9X7393B4YZ';
      console.log('Verwende Fallback-API-Schlüssel für Debugging');
      const API_URL = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${LOCATION}?unitGroup=metric&include=days&key=${fallbackKey}&contentType=json`;
      return fetchAndProcessData(API_URL);
    }
    
    const API_URL = `https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/${LOCATION}?unitGroup=metric&include=days&key=${API_KEY}&contentType=json`;
    return fetchAndProcessData(API_URL);
  };
  
  /**
   * Hilfsfunktion zum Abrufen und Verarbeiten der Wetterdaten
   * @param {string} url - Die API-URL
   * @returns {Promise<{current: Object, forecast: Array, weekend: Array}>} Formatierte Wetterdaten
   */
  async function fetchAndProcessData(url) {
    try {
      console.log('Fetching data from URL:', url.replace(/key=([^&]+)/, 'key=****'));
      
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`HTTP error! Status: ${response.status}, Details:`, errorText);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.days || !Array.isArray(data.days) || data.days.length === 0) {
        console.error('Unerwartete API-Antwortstruktur - keine Tagesdaten gefunden:', data);
        throw new Error('Unerwartete API-Antwortstruktur - keine Tagesdaten gefunden');
      }
      
      // Aktuelle Wetterdaten vom ersten Tag (heute) extrahieren
      const today = data.days[0];
      
      // Aktuelle Wetterdaten formatieren
      const currentWeather = {
        sonneneinstrahlung: today.solarradiation || 0,
        niederschlag: today.precip || 0,
        luftfeuchtigkeit: today.humidity || 0,
        windgeschwindigkeit: today.windspeed || 0,
        monat: new Date().getMonth() + 1, // Aktueller Monat (1-12)
        temperatur: today.temp || 0,
        temperaturtag: today.tempmax || 0,
        temperaturnacht: today.tempmin || 0,
        regenwahrscheinlichkeit: today.precipprob || 0,
        sonnenstunden: calculateSunshineHours(today)
      };
      
      // Wochenend-Daten extrahieren (nächster Freitag, Samstag, Sonntag)
      const weekendDays = getUpcomingWeekendDays(data.days);
      
      // Prognosedaten für die nächsten 14 Tage formatieren
      const forecast = data.days.slice(1, 15).map(day => ({
        datum: day.datetime,
        sonneneinstrahlung: day.solarradiation || 0,
        niederschlag: day.precip || 0,
        luftfeuchtigkeit: day.humidity || 0,
        windgeschwindigkeit: day.windspeed || 0,
        temperatur: day.temp || 0,
        temperaturtag: day.tempmax || 0,
        temperaturnacht: day.tempmin || 0,
        regenwahrscheinlichkeit: day.precipprob || 0,
        sonnenstunden: calculateSunshineHours(day),
        wochentag: getDayOfWeek(day.datetime)
      }));
      
      return {
        current: currentWeather,
        forecast: forecast,
        weekend: weekendDays
      };
    } catch (error) {
      console.error('Detailed error in fetchAndProcessData:', error);
      
      // Werfen Sie den Fehler mit mehr Kontext
      throw new Error(`Fehler beim Abrufen der Wetterdaten: ${error.message}`);
    }
  }
  
  /**
   * Berechnet die ungefähre Anzahl der Sonnenstunden basierend auf Sonnenauf- und -untergang
   * und dem Bewölkungsgrad
   * @param {Object} day - Tagesdaten aus der API
   * @returns {number} Geschätzte Sonnenstunden
   */
  function calculateSunshineHours(day) {
    if (!day.sunrise || !day.sunset) return 0;
    
    // Berechnung der Tageslänge in Stunden
    const sunrise = new Date(`2000-01-01T${day.sunrise}`);
    const sunset = new Date(`2000-01-01T${day.sunset}`);
    const daylengthHours = (sunset - sunrise) / (1000 * 60 * 60);
    
    // Berechnung der Sonnenstunden basierend auf Bewölkung
    // 100% Bewölkung = 0% der möglichen Sonnenstunden, 0% Bewölkung = 100% der möglichen Sonnenstunden
    const cloudCoverPercent = day.cloudcover || 0;
    const sunshineFactor = (100 - cloudCoverPercent) / 100;
    
    return Math.round(daylengthHours * sunshineFactor * 10) / 10; // Auf eine Dezimalstelle gerundet
  }
  
  /**
   * Findet das nächste Wochenende (Freitag, Samstag, Sonntag) in den Vorhersagedaten
   * @param {Array} days - Tagesvorhersagen aus der API
   * @returns {Array} Array mit Daten für das nächste Wochenende
   */
  function getUpcomingWeekendDays(days) {
    const weekendDays = [];
    const today = new Date();
    
    // Durchlaufe die Tage und suche nach dem nächsten Freitag, Samstag und Sonntag
    for (const day of days) {
      const date = new Date(day.datetime);
      const dayOfWeek = date.getDay(); // 0 = Sonntag, 1 = Montag, ..., 5 = Freitag, 6 = Samstag
      
      // Wenn der Tag in der Zukunft liegt und ein Freitag (5), Samstag (6) oder Sonntag (0) ist
      if (date > today && (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0)) {
        weekendDays.push({
          datum: day.datetime,
          wochentag: getDayOfWeek(day.datetime),
          sonneneinstrahlung: day.solarradiation || 0,
          niederschlag: day.precip || 0,
          luftfeuchtigkeit: day.humidity || 0,
          windgeschwindigkeit: day.windspeed || 0,
          temperatur: day.temp || 0,
          temperaturtag: day.tempmax || 0,
          temperaturnacht: day.tempmin || 0,
          regenwahrscheinlichkeit: day.precipprob || 0,
          sonnenstunden: calculateSunshineHours(day)
        });
        
        // Wenn wir einen kompletten Satz von Wochenendtagen haben (Freitag, Samstag, Sonntag), hören wir auf zu suchen
        if (weekendDays.length === 3) {
          break;
        }
      }
    }
    
    // Sortiere die Wochenendtage nach Datum
    weekendDays.sort((a, b) => new Date(a.datum) - new Date(b.datum));
    
    return weekendDays;
  }
  
  /**
   * Gibt den deutschen Wochentag für ein Datum zurück
   * @param {string} dateStr - Datumstring im Format 'YYYY-MM-DD'
   * @returns {string} Deutscher Wochentag
   */
  function getDayOfWeek(dateStr) {
    const date = new Date(dateStr);
    const weekdays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    return weekdays[date.getDay()];
  }
  
  /**
   * Abrufen von Testdaten für den Fall, dass die API nicht verfügbar ist
   * @returns {{current: Object, forecast: Array, weekend: Array}} Testdaten
   */
  export const getFallbackData = () => {
    const currentMonth = new Date().getMonth() + 1;
    
    // Testdaten für das Wochenende
    const weekend = [
      {
        datum: getNextFriday(),
        wochentag: 'Freitag',
        sonneneinstrahlung: 180,
        niederschlag: 0,
        luftfeuchtigkeit: 65,
        windgeschwindigkeit: 1.5,
        temperatur: 18,
        temperaturtag: 22,
        temperaturnacht: 14,
        regenwahrscheinlichkeit: 10,
        sonnenstunden: 6.5
      },
      {
        datum: getNextSaturday(),
        wochentag: 'Samstag',
        sonneneinstrahlung: 200,
        niederschlag: 0,
        luftfeuchtigkeit: 60,
        windgeschwindigkeit: 1.2,
        temperatur: 20,
        temperaturtag: 24,
        temperaturnacht: 15,
        regenwahrscheinlichkeit: 5,
        sonnenstunden: 7.2
      },
      {
        datum: getNextSunday(),
        wochentag: 'Sonntag',
        sonneneinstrahlung: 160,
        niederschlag: 0.2,
        luftfeuchtigkeit: 70,
        windgeschwindigkeit: 2.1,
        temperatur: 17,
        temperaturtag: 21,
        temperaturnacht: 13,
        regenwahrscheinlichkeit: 30,
        sonnenstunden: 5.5
      }
    ];
    
    // Aktuelle Testdaten
    const current = {
      sonneneinstrahlung: 150,
      niederschlag: 0,
      luftfeuchtigkeit: 70,
      windgeschwindigkeit: 2,
      monat: currentMonth,
      temperatur: 16,
      temperaturtag: 20,
      temperaturnacht: 12,
      regenwahrscheinlichkeit: 15,
      sonnenstunden: 6.0
    };
    
    // 14-Tage-Prognose als Testdaten
    const forecast = Array(14).fill(0).map((_, index) => {
      const date = new Date();
      date.setDate(date.getDate() + index + 1);
      const dateStr = date.toISOString().split('T')[0];
      const weekday = getDayOfWeek(dateStr);
      
      // Zufällige Werte erzeugen, um verschiedene Wetterbedingungen zu simulieren
      const sunnyDay = Math.random() > 0.3;
      const solarRadiation = sunnyDay ? 150 + Math.random() * 150 : 50 + Math.random() * 100;
      const temp = 15 + Math.random() * 10;
      const precipProb = sunnyDay ? Math.random() * 20 : 20 + Math.random() * 60;
      
      return {
        datum: dateStr,
        wochentag: weekday,
        sonneneinstrahlung: solarRadiation,
        niederschlag: precipProb > 50 ? 0.1 + Math.random() * 0.5 : 0,
        luftfeuchtigkeit: 50 + Math.random() * 30,
        windgeschwindigkeit: 0.5 + Math.random() * 3,
        temperatur: temp,
        temperaturtag: temp + 2 + Math.random() * 3,
        temperaturnacht: temp - 3 - Math.random() * 3,
        regenwahrscheinlichkeit: precipProb,
        sonnenstunden: sunnyDay ? 5 + Math.random() * 3 : 2 + Math.random() * 3
      };
    });
    
    return {
      current,
      forecast,
      weekend
    };
  };
  
  /**
   * Hilfsfunktion zum Berechnen des nächsten Freitags
   * @returns {string} Datum im Format YYYY-MM-DD
   */
  function getNextFriday() {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sonntag, ..., 5 = Freitag, 6 = Samstag
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
    
    const friday = new Date(today);
    friday.setDate(today.getDate() + daysUntilFriday);
    
    return friday.toISOString().split('T')[0];
  }
  
  /**
   * Hilfsfunktion zum Berechnen des nächsten Samstags
   * @returns {string} Datum im Format YYYY-MM-DD
   */
  function getNextSaturday() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7;
    
    const saturday = new Date(today);
    saturday.setDate(today.getDate() + daysUntilSaturday);
    
    return saturday.toISOString().split('T')[0];
  }
  
  /**
   * Hilfsfunktion zum Berechnen des nächsten Sonntags
   * @returns {string} Datum im Format YYYY-MM-DD
   */
  function getNextSunday() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = (0 - dayOfWeek + 7) % 7;
    
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + daysUntilSunday);
    
    return sunday.toISOString().split('T')[0];
  }